/**
 * AppShell — header + main content area.
 *
 * Block 6.A / ADR-024 D7 lineage; revised through:
 *   Block 6.C.1 (steward-gated Lookups nav link)
 *   Block 6.C.2 (Suggestions / My suggestions nav links)
 *   Notification-bell mini (count badge; 2026-05-08)
 *   Block 6.F.2 (β2-b sage-500 sticky header + user-menu dropdown)
 *   Block 6.F.5 ("Suggestions" → "Admin review" rename; badge query key
 *     transition to ['admin-review', 'badge-count'])
 *   Phase X.3.7 ("Admin review" → "Admin" rename; role subtitle removed
 *     from the account dropdown)
 *   Phase 1.7 (header redesign): "Peace Resources / Internal resource hub"
 *     brand + Home + Suggest + bell-with-count + user-menu cluster.
 *   Phase X.5.1 (HUB REBRAND + HEADER STRIP):
 *     - Brand: cream Peace dove (HubBrandMark) + "Olive"
 *       wordmark, no subtitle, on a sage-400 nav (the brand-designated
 *       nav color). Replaces the "P" block + "Peace Resources / Internal
 *       resource hub" stack app-wide — the hub is now the home surface.
 *       Brand mark routes to the in-app /t/{slug}/hub.
 *     - Header cluster STRIPPED to brand + notification bell + avatar/user
 *       menu ONLY (operator direction). Removed: the Home button (brand
 *       mark is home), the AI Tools button (reachable via its hub tile),
 *       the "+ Suggest Resource" button (relocated to the Directory page),
 *       and the pending-count badge + its badge-count query. The user menu
 *       keeps the admin-review / my-suggestions items + Sign out. The
 *       not_provisioned guard is unchanged.
 *   Phase X.5.2 (NOTIFICATION BELL): the header bell is now the
 *     announcements notification (NotificationBell) — an unread badge from
 *     the announcements query + a dropdown of recent announcements; opening
 *     it marks them seen. It no longer navigates to the review queue (that
 *     stays reachable from the user menu). Self-gates on view_staff_tools.
 *   Phase X.6.2 (DESIGN-REFRESH SHARED CHROME): the bar flips sage→cream
 *     (deliberate, prototype-approved; the X.5.1 sage nav is retired) with a
 *     1px forest hairline, x.6.1 tokens throughout, and Lucide icons. RE-adds
 *     a center pill nav (Home / AI Tools / Directory, active state from the
 *     route via @/lib/navSection) — reversing the X.5.1 header strip — plus a
 *     desktop search button. Brand keeps the "Olive" wordmark (Playfair,
 *     forest). Below 760px
 *     (useIsMobile) the center nav is replaced by a fixed bottom MobileTabBar.
 *     Bell + user-menu behavior and capability gating are unchanged; only the
 *     skin moved.
 *   Nav rework (2026-07-13): responsive nav on BOTH surfaces. Desktop center
 *     nav is <PrimaryNav> — pills in priority order that collapse into a "More"
 *     dropdown when they can't fit (the bar never pushes the page wider than the
 *     viewport). Mobile keeps the fixed bottom <MobileTabBar>, now capped at
 *     five slots (Home, Clients, Directory, Writing + a "More" sheet for the
 *     tail) so labels never collide or bleed off-screen. Order is
 *     founder-directed through Workspace, GC-ruled on the tail (Home, Clients,
 *     Directory, Writing, Workspace, Dashboard, Invoices, Reports); the old
 *     CareTree-fed Dashboard page was consolidated into the native operations
 *     Dashboard (/reports/operating). Gating + the search/bell/user-menu
 *     cluster are unchanged.
 *
 * §m — affordance gates here consume `canSuggest` / `canAddLookup` from
 * src/lib/capabilities.ts + src/lib/lookups.ts (Phase 1.11). The single
 * frontend mirror of the role × capability matrix lives there.
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Search } from 'lucide-react';
import { useTenant } from '@/lib/auth';
import { NotProvisionedLanding } from '@/components/NotProvisionedLanding';
import { NotificationBell } from '@/components/NotificationBell';
import { PrimaryNav } from '@/components/PrimaryNav';
import { MobileTabBar } from '@/components/MobileTabBar';
import { GlobalSearch, globalSearchGroups } from '@/components/GlobalSearch';
import { useIsMobile } from '@/hooks/useIsMobile';
import { visibleNavLinks, userMenuLinks, activeNavSection } from '@/lib/navSection';
import { useTenantConfig } from '@/hooks/useTenantConfig';
import { feedbackContactEmail, feedbackMailto } from '@/lib/tenantConfig';
import { signOut } from '@/lib/signOut';
import { loginForUnprovisioned, signedInName } from '@/lib/signedInName';


function initialOf(displayName: string | null | undefined): string {
  if (!displayName) return '?';
  const trimmed = displayName.trim();
  if (!trimmed) return '?';
  return trimmed.charAt(0).toUpperCase();
}

export interface AppShellProps {
  children: ReactNode;
}

export function AppShell({ children }: AppShellProps): ReactNode {
  const { principal, whoami, tenantSlug } = useTenant();

  // Feedback mailto address from tenant config (features.feedback_contact_email).
  const { data: tenantConfig } = useTenantConfig(tenantSlug);
  const feedbackEmail = feedbackContactEmail(tenantConfig);

  // The user-menu route items derive from the nav registry (UX-7): Admin
  // (steward) XOR My suggestions (writer) is encoded in the entries' gates.
  // (X.5.2: the header bell is the announcements notification, not a queue
  // link — see NotificationBell.)
  const menuLinks = userMenuLinks(whoami);

  // Memoized so PrimaryNav's measurement effect (keyed on `links`) doesn't
  // tear down + resubscribe its ResizeObserver on every AppShell render —
  // visibleNavLinks returns a fresh array otherwise (PR #529 review).
  const navLinks = useMemo(() => visibleNavLinks(whoami), [whoami]);

  const isMobile = useIsMobile();
  const { pathname } = useLocation();
  const activeSection = activeNavSection(pathname);

  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchGroups = globalSearchGroups(whoami);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Close menu on click-outside.
  useEffect(() => {
    if (!menuOpen) return;
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  const ownName = signedInName(whoami, principal);
  const userInitial = initialOf(ownName);

  // Phase 1.6 — not_provisioned route guard. AppShell renders the
  // access-denied landing instead of the main shell so the user doesn't
  // see a piecemeal 403-filled resource page. Per design plan r2 §B / r3 §C.
  // The guard sits BELOW every hook (hooks-before-gate): whoami resolves
  // async, and an early return above the hooks renders fewer hooks on the
  // re-render — React #310, page down to the error boundary.
  if (whoami?.outcome === 'not_provisioned') {
    return <NotProvisionedLanding principalDisplayName={loginForUnprovisioned(principal)} />;
  }

  return (
    <div className="min-h-screen flex flex-col text-ink">
      {/* X.6.2 chrome restyle: solid cream bar (NOT the X.5.1 sage nav — a
          deliberate, prototype-approved sage→cream flip), 1px forest hairline,
          pill nav. Brand is the "Olive" wordmark. */}
      <header className="sticky top-0 z-50 bg-cream border-b border-sage-800/[8%]">
        <div
          className={
            'max-w-6xl mx-auto px-5 flex items-center ' +
            (isMobile ? 'min-h-[60px] gap-3' : 'min-h-[68px] gap-6')
          }
        >
          {/* Brand: the dove with a martini + "Olive" wordmark (Playfair, forest).
              Links to the in-app hub. */}
          <Link
            to={`/t/${tenantSlug}/hub`}
            className="flex min-w-0 items-center gap-2.5 cursor-pointer rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
            aria-label="Olive home"
          >
            <img src="/brand/olive-dove-martini.png" alt="" className="h-9 w-auto" data-testid="app-brand-mark" />
            <span className="min-w-0 truncate font-display text-[1.1875rem] font-semibold leading-tight tracking-[-0.01em] whitespace-nowrap text-sage-800">
              Olive
            </span>
          </Link>

          {/* Desktop priority + overflow nav (nav rework 2026-07-13): pills in
              founder-priority order; the tail collapses into a "More" dropdown
              when they don't fit the bar (never pushing the page wider than the
              viewport). Below 760px the fixed bottom MobileTabBar replaces it. */}
          {!isMobile && principal && (
            <PrimaryNav links={navLinks} tenantSlug={tenantSlug} activeSection={activeSection} />
          )}

          {/* Right cluster: search (desktop) + announcements bell + avatar/user menu. */}
          {principal && (
            <div className="ml-auto flex shrink-0 items-center gap-2.5" ref={menuRef}>
              {/* The top search (PR 6a): clients, staff and the Directory,
                  each group only for a seat that can read it. On every
                  width; the mobile Directory tab stays as it was. */}
              <button
                type="button"
                onClick={() => setSearchOpen(true)}
                aria-label={
                  searchGroups.clients || searchGroups.staff
                    ? 'Search clients, staff and the Directory'
                    : 'Search the Directory'
                }
                aria-haspopup="dialog"
                data-testid="chrome-search"
                className="w-10 h-10 rounded-pill border border-sage-800/[8%] bg-paper text-sage-400 inline-flex items-center justify-center shadow-sh1 transition-all duration-[var(--dur-base)] ease-quiet motion-reduce:transition-none hover:text-sage-800 hover:border-sage-400/20 hover:shadow-sh2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
              >
                <Search size={19} aria-hidden="true" />
              </button>
              {searchOpen && <GlobalSearch onClose={() => setSearchOpen(false)} />}

              {/* X.5.2 — announcements notification bell (self-gates on
                  view_staff_tools; mso_staff sees none). */}
              <NotificationBell />

              {/* Avatar: forest circle, cream initial, gold ring. Click → user menu. */}
              <button
                type="button"
                onClick={() => setMenuOpen((v) => !v)}
                aria-label="Open user menu"
                aria-expanded={menuOpen}
                aria-haspopup="menu"
                className="w-10 h-10 rounded-pill bg-sage-800 text-cream flex items-center justify-center text-[15px] font-semibold cursor-pointer ring-[3px] ring-warm-400/30 ring-offset-2 ring-offset-paper transition-transform duration-[var(--dur-base)] ease-quiet motion-reduce:transition-none hover:scale-[1.04] focus-visible:outline-none focus-visible:ring-sage-400"
                title={ownName || undefined}
              >
                {userInitial}
              </button>

              {menuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
                  <div
                    role="menu"
                    aria-label="User menu"
                    className="absolute right-5 top-[68px] bg-paper rounded-2xl shadow-sh3 border border-sage-800/[8%] py-2 min-w-[232px] z-50"
                  >
                    <div className="px-4 py-2 border-b border-sage-800/[8%]">
                      {/* UX-13 §3 never-silently rule: truncated names carry title. */}
                      <p className="text-sm font-semibold text-sage-800 truncate" title={ownName || undefined}>
                        {ownName}
                      </p>
                    </div>

                    {menuLinks.map((link) => (
                      <Link
                        key={link.section}
                        role="menuitem"
                        to={`/t/${tenantSlug}${link.suffix}`}
                        onClick={() => setMenuOpen(false)}
                        aria-label={link.label}
                        className="block w-full text-left px-4 py-2.5 text-sm text-ink hover:bg-sand transition-colors"
                      >
                        {link.label}
                      </Link>
                    ))}

                    {/* Feedback — open to every authenticated user (the menu
                        only renders when `principal` is set). v1 mailto; styled
                        like Admin / My suggestions. Hidden when the tenant has
                        no feedback_contact_email configured. */}
                    {feedbackEmail && (
                      <a
                        role="menuitem"
                        href={feedbackMailto(feedbackEmail)}
                        onClick={() => setMenuOpen(false)}
                        aria-label="Feedback"
                        className="block w-full text-left px-4 py-2.5 text-sm text-ink hover:bg-sand transition-colors"
                      >
                        Feedback
                      </a>
                    )}

                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setMenuOpen(false);
                        void signOut('chosen');
                      }}
                      className="block w-full text-left px-4 py-2.5 text-sm text-ink-soft hover:bg-sand transition-colors border-t border-sage-800/[8%]"
                    >
                      Sign out
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </header>

      {/* Reserve space on mobile so the fixed MobileTabBar never covers content. */}
      <main className={isMobile ? 'flex-1 pb-28' : 'flex-1'}>{children}</main>

      {isMobile && principal && <MobileTabBar />}
    </div>
  );
}
